import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Paper,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';

const Privacy = () => {
  const { t } = useTranslation();

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Breadcrumbs sx={{ mb: 4 }} aria-label="breadcrumb">
        <Link color="inherit" href="/">
          {t('common.home', 'Inicio')}
        </Link>
        <Typography color="text.primary">{t('legal.privacy', 'Política de Privacidad')}</Typography>
      </Breadcrumbs>

      <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('legal.privacy', 'Política de Privacidad')}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          {t('legal.lastUpdated', 'Última actualización')}: 17/05/2023
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            1. {t('legal.privacyIntroduction', 'Introducción')}
          </Typography>
          <Typography paragraph>
            {t('legal.privacyIntroductionText', 'En TuttoGym nos tomamos muy en serio la privacidad de nuestros usuarios. Esta Política de Privacidad describe cómo recopilamos, utilizamos, procesamos y protegemos tu información personal cuando utilizas nuestros servicios, ya sea a través de nuestro sitio web, aplicación móvil u otras plataformas.')}
          </Typography>

          <Typography variant="h6" gutterBottom>
            2. {t('legal.dataCollection', 'Información que recopilamos')}
          </Typography>
          <Typography paragraph>
            {t('legal.dataCollectionText', 'Podemos recopilar la siguiente información personal:')}
          </Typography>
          <List dense sx={{ pl: 2, mb: 2 }}>
            <ListItem>
              <ListItemText primary={t('legal.dataCollection1', 'Información de registro: nombre, apellidos, dirección de correo electrónico, contraseña.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataCollection2', 'Información de perfil: edad, género, intereses deportivos, foto de perfil (opcional).')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataCollection3', 'Datos de facturación: información de pago, historial de transacciones.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataCollection4', 'Datos de uso: información sobre cómo utilizas nuestros servicios, gimnasios que visitas, actividades que reservas.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataCollection5', 'Información técnica: dirección IP, tipo de navegador, dispositivo, sistema operativo.')} />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom>
            3. {t('legal.dataUse', 'Cómo utilizamos tu información')}
          </Typography>
          <Typography paragraph>
            {t('legal.dataUseText', 'Utilizamos la información recopilada para:')}
          </Typography>
          <List dense sx={{ pl: 2, mb: 2 }}>
            <ListItem>
              <ListItemText primary={t('legal.dataUse1', 'Proporcionar, mantener y mejorar nuestros servicios.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataUse2', 'Procesar pagos y gestionar tu suscripción.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataUse3', 'Personalizar tu experiencia y proporcionarte recomendaciones.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataUse4', 'Comunicarnos contigo sobre actualizaciones, ofertas y eventos.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataUse5', 'Detectar, prevenir y abordar problemas técnicos, fraudes o actividades ilegales.')} />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom>
            4. {t('legal.dataSharing', 'Compartición de datos')}
          </Typography>
          <Typography paragraph>
            {t('legal.dataSharingText', 'Podemos compartir tu información con:')}
          </Typography>
          <List dense sx={{ pl: 2, mb: 2 }}>
            <ListItem>
              <ListItemText primary={t('legal.dataSharing1', 'Gimnasios y centros deportivos asociados para validar tu acceso y reservas.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataSharing2', 'Proveedores de servicios que nos ayudan a proporcionar nuestros servicios (procesamiento de pagos, análisis, marketing).')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.dataSharing3', 'Autoridades legales cuando sea requerido por la ley o para proteger nuestros derechos.')} />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom>
            5. {t('legal.dataSecurity', 'Seguridad de los datos')}
          </Typography>
          <Typography paragraph>
            {t('legal.dataSecurityText', 'Implementamos medidas de seguridad técnicas, administrativas y físicas diseñadas para proteger la información personal que recopilamos. Sin embargo, ningún sistema es completamente seguro, por lo que no podemos garantizar la seguridad absoluta de tu información.')}
          </Typography>

          <Typography variant="h6" gutterBottom>
            6. {t('legal.dataRetention', 'Retención de datos')}
          </Typography>
          <Typography paragraph>
            {t('legal.dataRetentionText', 'Conservamos tu información personal mientras mantengas una cuenta activa o según sea necesario para proporcionarte servicios. También podemos retener cierta información para cumplir con obligaciones legales, resolver disputas y hacer cumplir nuestros acuerdos.')}
          </Typography>

          <Typography variant="h6" gutterBottom>
            7. {t('legal.yourRights', 'Tus derechos')}
          </Typography>
          <Typography paragraph>
            {t('legal.yourRightsText', 'Dependiendo de tu ubicación, puedes tener ciertos derechos relacionados con tu información personal, incluyendo:')}
          </Typography>
          <List dense sx={{ pl: 2, mb: 2 }}>
            <ListItem>
              <ListItemText primary={t('legal.yourRights1', 'Acceder a tu información personal.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.yourRights2', 'Corregir información inexacta.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.yourRights3', 'Eliminar tu información en determinadas circunstancias.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.yourRights4', 'Oponerte al procesamiento de tu información.')} />
            </ListItem>
            <ListItem>
              <ListItemText primary={t('legal.yourRights5', 'Retirar tu consentimiento en cualquier momento.')} />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom>
            8. {t('legal.cookies', 'Cookies y tecnologías similares')}
          </Typography>
          <Typography paragraph>
            {t('legal.cookiesText', 'Utilizamos cookies y tecnologías similares para mejorar tu experiencia, analizar el tráfico y personalizar el contenido. Puedes gestionar tus preferencias de cookies a través de la configuración de tu navegador.')}
          </Typography>

          <Typography variant="h6" gutterBottom>
            9. {t('legal.privacyChildren', 'Privacidad de los niños')}
          </Typography>
          <Typography paragraph>
            {t('legal.privacyChildrenText', 'Nuestros servicios no están dirigidos a personas menores de 16 años. No recopilamos a sabiendas información personal de niños menores de 16 años. Si descubrimos que hemos recopilado información personal de un niño menor de 16 años, eliminaremos esa información lo antes posible.')}
          </Typography>

          <Typography variant="h6" gutterBottom>
            10. {t('legal.privacyChanges', 'Cambios en esta Política de Privacidad')}
          </Typography>
          <Typography paragraph>
            {t('legal.privacyChangesText', 'Podemos actualizar nuestra Política de Privacidad de vez en cuando. Te notificaremos cualquier cambio publicando la nueva Política de Privacidad en esta página y, si los cambios son significativos, te proporcionaremos un aviso más prominente o te enviaremos una notificación por correo electrónico.')}
          </Typography>
        </Box>

        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" color="text.secondary">
            {t('legal.contactForQuestions', 'Si tienes alguna pregunta sobre esta Política de Privacidad, por favor contáctanos en')}: <Link href="mailto:privacidad@tuttogym.com">privacidad@tuttogym.com</Link>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Privacy; 